import { useTranslation } from '@gimlite/common';
import {
  CellContext,
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  RowSelectionState,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import classNames from 'classnames';
import {
  forwardRef,
  memo,
  useEffect,
  useId,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Checkbox } from '../checkbox/checkbox.component';
import { Col } from '../col/col.component';
import { Empty } from '../empty/empty.component';
import { Icon, IconType } from '../icon/icon.component';
import { Row } from '../row/row.component';
import { Spinner } from '../spinner/spinner.component';
import { Text } from '../text/text.component';
import './table.component.scss';

const SorterIcon = memo(
  ({ direction }: { direction?: 'asc' | 'desc' | false }) => {
    return (
      <Col gap={2} alignItems="center" justifyContent="center">
        <Icon
          variant="caret_up"
          size="initial"
          color={!direction || direction === 'asc' ? 'black_50' : 'deep_indigo'}
        />
        <Icon
          variant="caret_down"
          size="initial"
          color={
            !direction || direction === 'desc' ? 'black_50' : 'deep_indigo'
          }
        />
      </Col>
    );
  },
);

SorterIcon.displayName = 'SorterIcon';

declare namespace TableButtonType {
  type Props = {
    label: string;
    icon?: IconType.Props['variant'];
    onClick?: () => void;
    disabled?: boolean | null;
  };
}

const TableButton = memo(
  ({ label, icon, onClick, disabled }: TableButtonType.Props) => {
    return (
      <div
        className={classNames('tableButton', {
          'tableButton--disabled': disabled,
        })}
        onClick={(e) => {
          e.stopPropagation();
          !disabled && onClick?.();
        }}
      >
        {icon && <Icon variant={icon} size="xs" color="deep_indigo" />}
        <Text variant="headcaption" color="deep_indigo">
          {label}
        </Text>
      </div>
    );
  },
);

TableButton.displayName = 'TableButton';

export declare namespace TableType {
  type Props<T extends Record<string, unknown>> = {
    className?: string | null;
    isLoading?: boolean;
    isError?: boolean;
    enableEmptyText?: boolean;
    data:
      | Array<T & { id: string; onClick?: (id: string) => void }>
      | {
          list: Array<T & { id: string; onClick?: (id: string) => void }>;
          paging: {
            current: number;
            count: number;
            limit: number;
          };
        }
      | null;
    actions?: Array<{
      disabled?: boolean | null;
      label: string;
      icon?: IconType.Props['variant'];
      onClick?: (ids: Array<string>) => void;
    }> | null;
    columns: Array<{
      defaultSort?: 'ASC' | 'DESC';
      header: string;
      accessorKey: keyof T;
      maxWidth?: number;
      cell?: (ctx: CellContext<any, any>) => React.ReactNode;
      enableSort?: boolean;
    }>;
    isSelectable?: boolean | null;
    onSelection?: (ids: Array<string>) => void;
    onLoadMore?: (paging: TableType.Helpers.Paging) => void;
    onSort?: (sorting: SortingState) => void;
    variant?: 'table' | 'list';
  };

  type Ref<T = any> = {
    setActiveRow: (id: string | null) => void;
    getPrevId: () => { id: string; data: T } | null;
    getNextId: () => { id: string; data: T } | null;
  };

  namespace Helpers {
    type Paging = {
      page: number;
      limit: number;
    };
    type MyColumnDef<T> = ColumnDef<T> & {
      maxWidth?: number;
      enableSort?: boolean;
    };
  }
}

const TableComponent = forwardRef(
  <T extends Record<string, unknown>>(
    {
      className,
      data,
      columns,
      isLoading,
      isSelectable = false,
      onSelection,
      isError,
      actions,
      enableEmptyText = true,
      onLoadMore,
      onSort,
      variant = 'table',
    }: TableType.Props<T>,
    ref: React.Ref<TableType.Ref>,
  ) => {
    const id = useId();
    const { t } = useTranslation();
    const [sorting, setSorting] = useState<SortingState>([]);
    const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
    const [activeRowId, setActiveRowId] = useState<string | null>(null);
    const parentRef = useRef<HTMLDivElement>(null);
    const loadMoreCalledRef = useRef<boolean>(false);
    const [dataTable, setDataTable] = useState<T[]>([]);
    const isInitialSortRef = useRef<boolean>(true);
    const previousSortingRef = useRef<SortingState>([]);

    const dataPaging = useMemo(
      () => (Array.isArray(data) ? null : data?.paging || null),
      [data],
    );

    const [isLoadingInternal, setIsLoadingInternal] = useState<boolean>(false);

    console.log('isLoadingInternal', isLoadingInternal);

    useImperativeHandle(ref, () => ({
      setActiveRow: (id: string | null) => {
        setActiveRowId(id);

        if (id) {
          const rowIndex = dataTable.findIndex(
            (item) => (item as T & { id: string }).id === id,
          );

          if (rowIndex !== -1) {
            rowVirtualizer.scrollToIndex(rowIndex, {
              align: 'center',
              behavior: 'smooth',
            });
          }
        }
      },
      getPrevId: () => {
        if (!activeRowId || dataTable.length === 0) return null;

        const currentIndex = dataTable.findIndex(
          (item) => (item as T & { id: string }).id === activeRowId,
        );

        if (currentIndex <= 0) return null;

        const prevItem = dataTable[currentIndex - 1] as T & { id: string };
        return { id: prevItem.id, data: prevItem as T };
      },
      getNextId: () => {
        if (!activeRowId || dataTable.length === 0) return null;

        const currentIndex = dataTable.findIndex(
          (item) => (item as T & { id: string }).id === activeRowId,
        );

        if (currentIndex === -1 || currentIndex >= dataTable.length - 1)
          return null;

        const nextItem = dataTable[currentIndex + 1] as T & { id: string };
        return { id: nextItem.id, data: nextItem as T };
      },
    }));

    const hasAnySortEnabled = useMemo(
      () => columns.some((col) => col.enableSort !== false),
      [columns],
    );

    const columnsWithSortConfig = useMemo(
      () =>
        columns.map((col) => ({
          ...col,
          enableSorting: col.enableSort !== false,
        })),
      [columns],
    );

    const table = useReactTable({
      data: dataTable || [],
      columns: columnsWithSortConfig,
      state: {
        sorting,
        rowSelection,
      },
      enableMultiSort: true,
      enableRowSelection: true,
      columnResizeMode: 'onEnd',
      enableSorting: hasAnySortEnabled,
      enableColumnResizing: true,
      onRowSelectionChange: setRowSelection,
      onSortingChange: setSorting,
      getCoreRowModel: getCoreRowModel(),
      getSortedRowModel: onSort ? undefined : getSortedRowModel(),
      getFilteredRowModel: getFilteredRowModel(),
      getRowId: (row) => (row as T & { id: string }).id,
      pageCount: dataPaging ? dataPaging.count : 1,
      manualPagination: true,
      manualSorting: !!onSort,
      onPaginationChange: () => {
        console.log('onPaginationChange');
      },
    });

    const rowVirtualizer = useVirtualizer({
      count: table.getRowModel().rows.length,
      getScrollElement: () => parentRef.current,
      estimateSize: () => 42,
      overscan: 5,
    });

    useEffect(() => {
      console.log('📜 onLoadMore Subject - USE EFFECT LOAD MORE');
      if (!onLoadMore || !dataPaging || dataPaging.count === 0) return;
      console.log('📜 onLoadMore Subject - DATA PAGING', {
        dataPaging,
      });

      const virtualItems = rowVirtualizer.getVirtualItems();
      console.log('📜 onLoadMore Subject - VIRTUAL ITEMS', virtualItems);
      if (virtualItems.length === 0) return;

      const lastItem = virtualItems[virtualItems.length - 1];
      console.log('📜 onLoadMore Subject - LAST ITEM', lastItem);
      const totalRows = dataTable?.length || 0;
      console.log('📜 onLoadMore Subject - TOTAL ROWS', totalRows);
      const isNearEnd = lastItem.index >= totalRows - 5;
      console.log('📜 onLoadMore Subject - IS NEAR END', isNearEnd);
      if (!isNearEnd) {
        loadMoreCalledRef.current = false;
        console.log(
          '📜 onLoadMore Subject - BEFORE isNearEnd FALSE setIsLoadingInternal(false)',
        );
        setIsLoadingInternal(false);
        return;
      }

      const hasMore = dataPaging.current * dataPaging.limit < dataPaging.count;

      console.log('📜 onLoadMore Subject - HAS MORE', hasMore);
      if (!hasMore) {
        console.log(
          '📜 onLoadMore Subject - BEFORE hasMore FALSE setIsLoadingInternal(false)',
        );
        setIsLoadingInternal(false);
        return;
      }

      if (loadMoreCalledRef.current) {
        console.log(
          '📜 onLoadMore Subject - BEFORE loadMoreCalledRef.current TRUE return',
        );
        return;
      }

      loadMoreCalledRef.current = true;
      console.log('📜 onLoadMore Subject - BEFORE setIsLoadingInternal(true)');
      setIsLoadingInternal(true);
      onLoadMore({ page: dataPaging.current + 1, limit: dataPaging.limit });
    }, [
      rowVirtualizer.getVirtualItems().length,
      dataTable?.length,
      dataPaging,
      onLoadMore,
    ]);

    useEffect(() => {
      if (onSelection) {
        onSelection(Object.keys(rowSelection));
      }
    }, [rowSelection]);

    useEffect(() => {
      // Ne pas appeler onSort lors de l'initialisation avec defaultSort
      if (!onSort || isInitialSortRef.current) return;

      // Comparer avec le tri précédent pour éviter les appels multiples
      const hasChanged =
        sorting.length !== previousSortingRef.current.length ||
        sorting.some((sort, index) => {
          const prev = previousSortingRef.current[index];
          return !prev || sort.id !== prev.id || sort.desc !== prev.desc;
        });

      if (hasChanged) {
        previousSortingRef.current = sorting;
        onSort(sorting);
      }
    }, [sorting, onSort]);

    useEffect(() => {
      setSorting((prev) => {
        if (prev.length > 0) return prev;

        const defaultSorting = columns.reduce((acc, column) => {
          if (column.defaultSort && column.enableSort !== false) {
            acc.push({
              id: column.accessorKey as string,
              desc: column.defaultSort === 'DESC',
            });
          }
          return acc;
        }, [] as SortingState);

        // Initialiser la référence avec le tri par défaut
        if (defaultSorting.length > 0) {
          previousSortingRef.current = defaultSorting;
        }

        return defaultSorting;
      });

      // Après l'initialisation, on active les callbacks onSort
      // Utilisation de setTimeout pour s'assurer que ça se passe après le render
      const timer = setTimeout(() => {
        isInitialSortRef.current = false;
      }, 0);

      return () => clearTimeout(timer);
    }, [columns]);

    useEffect(() => {
      if (Array.isArray(data)) {
        setDataTable(() => data);
      } else if (data?.list && data?.paging?.current === 1) {
        setDataTable(() => data.list);

        if (parentRef.current) {
          parentRef.current.scrollTop = 0;
        }
      } else if (data?.list && data?.paging?.current > 1) {
        setDataTable((prev) => [...prev, ...data.list]);
      }
    }, [data]);

    if (isError) {
      return (
        <Col height="100%" alignItems="center" justifyContent="center">
          <Empty variant="errorServer" />
        </Col>
      );
    }

    if (
      !isLoadingInternal &&
      (!data ||
        (Array.isArray(data) && isLoading) ||
        (!Array.isArray(data) &&
          data?.paging?.current === 1 &&
          data?.paging?.count !== 0 &&
          isLoading))
    ) {
      return (
        <Col height="100%" alignItems="center" justifyContent="center">
          <Spinner />
        </Col>
      );
    }

    if (
      !isLoadingInternal &&
      !isLoading &&
      ((Array.isArray(data) && data.length === 0) ||
        (!Array.isArray(data) && data?.paging && data.paging.count === 0))
    ) {
      return (
        <Col height="100%" alignItems="center" justifyContent="center">
          <Empty variant="notData" />
        </Col>
      );
    }

    return (
      <div
        ref={parentRef}
        className={classNames('table', `table--variant--${variant}`, className)}
      >
        <table className="table__native">
          <thead>
            {Object.keys(rowSelection).length === 0 ? (
              table.getHeaderGroups().map((headerGroup) => (
                <tr
                  className="table__native__headerUnselect"
                  key={headerGroup.id}
                >
                  {isSelectable && (
                    <th
                      onClick={(e) => e.stopPropagation()}
                      className="checkbox-cell"
                    >
                      <Checkbox
                        checked={table.getIsSomeRowsSelected()}
                        onChange={({ checked }) => {
                          table.toggleAllRowsSelected(checked);
                        }}
                        variant="deselect"
                        size="xs"
                      />
                    </th>
                  )}
                  {headerGroup.headers.map((header) => {
                    const textHeader = flexRender(
                      header.column.columnDef.header,
                      header.getContext(),
                    );

                    const columnDef = header.column
                      .columnDef as TableType.Helpers.MyColumnDef<T>;
                    const maxWidth = columnDef.maxWidth;
                    const enableSort = columnDef.enableSort !== false;

                    return (
                      <th
                        key={header.id}
                        onClick={
                          enableSort
                            ? header.column.getToggleSortingHandler()
                            : undefined
                        }
                        style={{
                          cursor: enableSort ? 'pointer' : 'default',
                          width: maxWidth ? `${maxWidth}px` : undefined,
                        }}
                      >
                        {textHeader && (
                          <Row gap={5} alignItems="center">
                            {enableSort && (
                              <SorterIcon
                                direction={header.column.getIsSorted()}
                              />
                            )}
                            <Text
                              variant="headcaption"
                              color="black_50"
                              whiteSpace="nowrap"
                            >
                              {flexRender(
                                header.column.columnDef.header,
                                header.getContext(),
                              )}
                            </Text>
                          </Row>
                        )}
                      </th>
                    );
                  })}
                </tr>
              ))
            ) : (
              <tr className="table__native__headerSelect">
                {isSelectable && (
                  <th className="checkbox-cell">
                    <div
                      onClick={(e) => {
                        e.stopPropagation();
                        table.resetRowSelection();
                      }}
                      className="checkbox-cell-unselect"
                    >
                      <Checkbox
                        className="checkbox-cell-unselect-check"
                        checked={Object.keys(rowSelection).length > 0}
                        variant="deselect"
                        size="xs"
                      />
                      <Text
                        className="checkbox-cell-label"
                        variant="headcaption"
                        color="deep_indigo"
                        whiteSpace="nowrap"
                      >
                        {`${t('watermelon-unselect')} (${Object.keys(rowSelection).length})`}
                      </Text>
                    </div>
                  </th>
                )}

                <th colSpan={columns.length}>
                  <div className="table__native__headerSelect__actions">
                    {actions?.map((action) => (
                      <TableButton
                        key={action.label}
                        {...action}
                        onClick={() =>
                          action.onClick?.(Object.keys(rowSelection))
                        }
                      />
                    ))}
                  </div>
                </th>
              </tr>
            )}
          </thead>

          <tbody>
            {table.getRowModel().rows.length === 0 ? null : (
              <>
                {rowVirtualizer.getVirtualItems()[0]?.start > 0 && (
                  <tr>
                    <td
                      colSpan={
                        table.getVisibleFlatColumns().length +
                        (isSelectable ? 1 : 0)
                      }
                      style={{
                        height: `${rowVirtualizer.getVirtualItems()[0].start}px`,
                      }}
                    />
                  </tr>
                )}

                {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                  const row = table.getRowModel().rows[virtualRow.index];
                  if (!row) return null;

                  const isActive = activeRowId === row.original.id;

                  return (
                    <tr
                      id={`tableNativeRow-${id}-${virtualRow.index}`}
                      key={row.id}
                      data-index={virtualRow.index}
                      className={classNames({
                        'table__native__row--selectable':
                          row?.original?.onClick,
                        'table__native__row--active': isActive,
                      })}
                      onClick={() => {
                        const onClick = row?.original?.onClick;
                        if (typeof onClick === 'function') {
                          setActiveRowId(
                            (row.original as T & { id: string }).id,
                          );
                          onClick((row.original as T & { id: string }).id);
                        }
                      }}
                    >
                      {isSelectable && (
                        <td
                          onClick={(e) => e.stopPropagation()}
                          className="checkbox-cell"
                        >
                          <Checkbox
                            size="xs"
                            checked={row.getIsSelected()}
                            onChange={({ checked }) => {
                              row.toggleSelected(checked);
                            }}
                          />
                        </td>
                      )}
                      {row.getVisibleCells().map((cell) => {
                        const maxWidth = (
                          cell.column
                            .columnDef as TableType.Helpers.MyColumnDef<T>
                        ).maxWidth;
                        return (
                          <td
                            key={cell.id}
                            style={{
                              width: maxWidth ? `${maxWidth}px` : undefined,
                            }}
                          >
                            <Row gap={5}>
                              {(() => {
                                const rawValue = cell.getValue();
                                const cellValue = flexRender(
                                  cell.column.columnDef.cell,
                                  cell.getContext(),
                                );

                                if (
                                  cell.column.columnDef.header &&
                                  enableEmptyText &&
                                  (rawValue === null ||
                                    rawValue === undefined ||
                                    rawValue === '' ||
                                    (typeof rawValue === 'string' &&
                                      rawValue.trim() === ''))
                                ) {
                                  return (
                                    <Text
                                      variant={
                                        variant === 'list'
                                          ? 'headcaption'
                                          : 'caption'
                                      }
                                      color="black_50"
                                      whiteSpace="nowrap"
                                    >
                                      N/A
                                    </Text>
                                  );
                                }

                                return (
                                  <Text
                                    variant={
                                      variant === 'list'
                                        ? 'headcaption'
                                        : 'caption'
                                    }
                                    color={
                                      variant === 'list'
                                        ? 'black_90'
                                        : 'deep_indigo'
                                    }
                                    whiteSpace="nowrap"
                                  >
                                    {cellValue}
                                  </Text>
                                );
                              })()}
                            </Row>
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}

                {(() => {
                  const lastItem = rowVirtualizer.getVirtualItems().at(-1);
                  if (!lastItem) return null;

                  const paddingBottom =
                    rowVirtualizer.getTotalSize() - lastItem.end;

                  return paddingBottom > 0 ? (
                    <tr>
                      <td
                        colSpan={
                          table.getVisibleFlatColumns().length +
                          (isSelectable ? 1 : 0)
                        }
                        style={{ height: `${paddingBottom}px` }}
                      />
                    </tr>
                  ) : null;
                })()}
              </>
            )}
          </tbody>
        </table>
        {isLoadingInternal && (
          <div className="table__loader">
            <Spinner />
          </div>
        )}
      </div>
    );
  },
) as <T extends Record<string, unknown>>(
  props: TableType.Props<T> & { ref?: React.Ref<TableType.Ref> },
) => React.ReactElement;

export const Table = memo(TableComponent) as typeof TableComponent;
export type { SortingState };
